<template>
  <Teleport to="body">
    <ModalComponent
      :submitButtonText="$t('forwards.deals.common.close')"
      size="lg"
      @close="close"
      @setForceToggle="setForceToggle"
    >
      <template #title>{{
        $t("forwards.sales_strategy.schedule_title")
      }}</template>

      <template #body>
        <div class="p-1" data-testid="sales-schedule">
          <p
            v-if="schedule === null"
            class="text-body-tertiary fs-10 mb-0"
            data-testid="sales-schedule-pending"
          >
            {{ $t("forwards.sales_strategy.schedule_pending") }}
          </p>

          <p
            v-else-if="schedule.length === 0"
            class="text-body-tertiary fs-10 mb-0"
            data-testid="sales-schedule-empty"
          >
            {{ $t("forwards.sales_strategy.schedule_empty") }}
          </p>

          <div
            v-for="timeline in schedule ?? []"
            :key="timeline.sales_year"
            class="mb-3"
            :data-testid="`sales-schedule-${timeline.sales_year}`"
          >
            <div class="fw-semibold fs-9 mb-1">
              {{
                $t("forwards.sales_strategy.schedule_timeline", {
                  year: timeline.sales_year,
                })
              }}
            </div>
            <div class="table-responsive">
              <table class="table table-sm mb-2 sales-timeline">
                <thead>
                  <tr>
                    <th scope="col">
                      {{ $t("forwards.sales_strategy.schedule_production") }}
                    </th>
                    <th
                      v-for="auction in timeline.auctions"
                      :key="auction.opens_on"
                      scope="col"
                      class="text-end text-nowrap"
                      :class="STATE_CLASSES[auction.state]"
                      :title="
                        $t('forwards.sales_strategy.schedule_opens_on', {
                          date: releaseDate(auction.opens_on),
                        })
                      "
                    >
                      {{
                        $t("forwards.sales_strategy.schedule_auction", {
                          quarter: auctionQuarter(auction.opens_on),
                        })
                      }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template
                    v-for="group in timeline.groups"
                    :key="group.production_year"
                  >
                    <!-- A group releasing its whole year needs no header saying which share it is. -->
                    <tr
                      v-if="group.share_pct < 100"
                      class="table-light"
                      data-testid="sales-schedule-group"
                    >
                      <td
                        :colspan="timeline.auctions.length + 1"
                        class="fw-semibold fs-10"
                      >
                        {{ groupTitle(group) }}
                        <span class="text-body-tertiary fw-normal">{{
                          groupNote(timeline.sales_year, group)
                        }}</span>
                      </td>
                    </tr>
                    <tr v-for="row in group.rows" :key="row.period_label">
                      <td class="text-nowrap">
                        {{ productionLabel(row.period_label) }}
                      </td>
                      <td
                        v-for="(mwh, index) in row.added_mwh"
                        :key="timeline.auctions[index].opens_on"
                        class="text-end"
                        :class="STATE_CLASSES[timeline.auctions[index].state]"
                      >
                        {{ formatNumberWithSpaces(mwh) }}
                      </td>
                    </tr>
                  </template>
                  <tr class="fw-semibold">
                    <td>{{ $t("forwards.sales_strategy.schedule_total") }}</td>
                    <td
                      v-for="auction in timeline.auctions"
                      :key="auction.opens_on"
                      class="text-end text-nowrap"
                      :class="STATE_CLASSES[auction.state]"
                    >
                      {{ formatNumberWithSpaces(auction.added_mwh) }}
                      <span class="fs-10 text-body-tertiary fw-normal">{{
                        $t("common.mwh")
                      }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </ModalComponent>
  </Teleport>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type {
  SalesTimeline,
  SalesTimelineGroup,
  SalesWindowState,
} from "@/forwards2/forwardsTypes";
import { MONTHS_PER_QUARTER, parsePeriodLabel } from "@/forwards2/periodUtils";
import { useSalesStrategyText } from "@/forwards2/salesStrategy";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The pool's sales timeline under its strategy, read from the editor's live preview: one table per
 * sales year, its auctions as columns and every listing they release as rows, each cell what that
 * auction adds to the listing's release. A pool that sells ahead releases two production years in
 * each sales year, so its rows sit under a header per production year naming the share released:
 * the early share of the year two ahead, the rest of the year directly ahead. Teleported to the
 * body because it opens from inside the pool editor, itself a modal.
 *
 * Producers see this, so it never mentions that staff can override the strategy.
 */
defineProps<{
  /** The preview's schedule; null while there is no preview to read it from. */
  schedule: SalesTimeline[] | null;
}>();

const model = defineModel<boolean>({ required: true });

// A past auction recedes and the current one is tinted, header to total, as v1 tinted its open sales; an upcoming one is plain.
const STATE_CLASSES: Record<SalesWindowState, string> = {
  passed: "text-body-tertiary",
  current: "is-current",
  upcoming: "",
};

const { t, locale } = useI18n();
const { releaseDate } = useSalesStrategyText();
const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};

watch(model, (isOpen) => forceToggle.value?.(isOpen));

function groupTitle(group: SalesTimelineGroup): string {
  const values = { year: group.production_year, percent: group.share_pct };
  return group.early
    ? t("forwards.sales_strategy.schedule_group_early", values)
    : t("forwards.sales_strategy.schedule_group_rest", values);
}

/** Where the year's other share is released: the next sales year after the early share, the previous one before the rest. */
function groupNote(salesYear: number, group: SalesTimelineGroup): string {
  const percent = 100 - group.share_pct;
  return group.early
    ? t("forwards.sales_strategy.schedule_group_early_note", {
        year: salesYear + 1,
        percent,
      })
    : t("forwards.sales_strategy.schedule_group_rest_note", {
        year: salesYear - 1,
        percent,
      });
}

/** Every auction opens on the first day of a quarter, so the quarter names it. */
const auctionQuarter = (opensOn: string): number =>
  Math.floor((Number(opensOn.slice(5, 7)) - 1) / MONTHS_PER_QUARTER) + 1;

/** `2027-Q1` → `2027 Q1`, `2027-H1` → `2027 H1`, `2027-01` → `2027 Jan`, `2027` stays. */
const productionLabel = (label: string): string => {
  const period = parsePeriodLabel(label);
  if (period === null) return label;
  if (period.width === "quarter") return `${period.year} Q${period.quarter}`;
  if (period.width === "half_year") return `${period.year} H${period.halfYear}`;
  if (period.width === "month") {
    const month = new Intl.DateTimeFormat(locale.value, {
      month: "short",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(period.year, (period.month ?? 1) - 1, 1)));
    return `${period.year} ${month}`;
  }
  return String(period.year);
};

const close = () => {
  model.value = false;
};
</script>

<style scoped lang="scss">
// The theme paints a cell's state through this variable, as `.table-active` does.
.sales-timeline .is-current {
  --phoenix-table-bg-state: var(--phoenix-primary-bg-subtle);
}
</style>
